<script setup lang="ts">
import { ref } from "vue"
import useBackend from "@/composition/useBackend"
import type { AppInfo } from "@/backend/apps.types"

const emit = defineEmits<{
  (e: "installed", app: AppInfo): void
}>()

const { apps: appsService } = useBackend()

const isDragging = ref(false)
const isUploading = ref(false)
const uploadError = ref<string | null>(null)
const selectedFile = ref<File | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)

function handleDragOver(event: DragEvent) {
  event.preventDefault()
  isDragging.value = true
}

function handleDragLeave(event: DragEvent) {
  event.preventDefault()
  isDragging.value = false
}

function handleDrop(event: DragEvent) {
  event.preventDefault()
  isDragging.value = false

  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    selectFile(files[0])
  }
}

function handleFileInput(event: Event) {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (files && files.length > 0) {
    selectFile(files[0])
  }
}

function selectFile(file: File) {
  uploadError.value = null

  if (!file.name.endsWith(".hapk")) {
    uploadError.value = "Please select a valid .hapk app package file"
    return
  }

  selectedFile.value = file
}

function clearSelection() {
  selectedFile.value = null
  uploadError.value = null
  if (fileInputRef.value) {
    fileInputRef.value.value = ""
  }
}

async function uploadPackage() {
  if (!selectedFile.value) return

  isUploading.value = true
  uploadError.value = null

  try {
    const result = await appsService.uploadPackage(selectedFile.value)
    emit("installed", result)
    clearSelection()
  } catch (error: unknown) {
    if (error && typeof error === "object" && "message" in error) {
      uploadError.value = (error as { message: string }).message
    } else {
      uploadError.value = "Failed to upload app package"
    }
  } finally {
    isUploading.value = false
  }
}

function openFilePicker() {
  fileInputRef.value?.click()
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
</script>

<template>
  <div class="marketplace-home">
    <div class="home-header">
      <h1 class="home-title">Apps</h1>
      <p class="home-subtitle txt-s">
        Manage your installed apps or install new ones from packages.
      </p>
    </div>

    <div class="home-content">
      <div class="box-content p2">
        <div class="upload-section">
          <div
            class="dropzone"
            :class="{
              'dropzone--dragging': isDragging,
              'dropzone--has-file': selectedFile,
            }"
            @dragover="handleDragOver"
            @dragleave="handleDragLeave"
            @drop="handleDrop"
          >
            <input
              ref="fileInputRef"
              type="file"
              accept=".hapk"
              class="file-input"
              @change="handleFileInput"
            />

            <template v-if="selectedFile">
              <div class="selected-file">
                <div class="file-icon">📦</div>
                <div class="file-info">
                  <span class="file-name">{{ selectedFile.name }}</span>
                  <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="dropzone-content">
                <div class="dropzone-icon">📦</div>
                <div class="dropzone-text">
                  <span class="dropzone-primary">
                    Drop .hapk file here or click to browse
                  </span>
                  <span class="dropzone-secondary">
                    Supports Hantera App Package (.hapk) files
                  </span>
                </div>
              </div>
            </template>
          </div>

          <div v-if="uploadError" class="box-content box-danger p2 txt-s">
            {{ uploadError }}
          </div>

          <div class="upload-actions">
            <button
              v-if="selectedFile"
              class="btn-secondary"
              @click.stop="clearSelection"
              :disabled="isUploading"
            >
              Clear
            </button>
            <button
              class="btn-primary"
              @click="uploadPackage"
              :disabled="!selectedFile || isUploading"
            >
              {{ isUploading ? "Installing..." : "Install App" }}
            </button>
          </div>
        </div>
      </div>

      <div class="box-content p2">
        <div class="marketplace-placeholder">
          <div class="placeholder-icon">🏪</div>
          <div class="placeholder-text">
            <span class="placeholder-title">Coming Soon</span>
            <span class="placeholder-description">
              The app marketplace will allow you to discover and install apps
              directly from Hantera's app repository.
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.marketplace-home {
  height: 100%;
  overflow: auto;
  padding: 1.5rem;
}

.home-header {
  margin-bottom: 1.5rem;
}

.home-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: hsl(var(--color-10));
}

.home-subtitle {
  margin: 0.25rem 0 0;
  color: hsl(var(--color-text-50));
}

.home-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dropzone {
  position: relative;
  border: 2px dashed hsl(var(--color-70));
  border-radius: 0.5rem;
  padding: 2rem;
  cursor: pointer;
  transition: border-color 150ms, background-color 150ms;
}

.dropzone:hover {
  border-color: hsl(var(--color-50));
  background-color: hsl(var(--color-95));
}

.dropzone--dragging {
  border-color: hsl(var(--color-primary));
  background-color: hsl(var(--color-primary-95));
}

.dropzone--has-file {
  border-style: solid;
  border-color: hsl(var(--color-primary));
  background-color: hsl(var(--color-primary-95));
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
}

.dropzone-icon {
  font-size: 2rem;
}

.dropzone-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.dropzone-primary {
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(var(--color-20));
}

.dropzone-secondary {
  font-size: 0.75rem;
  color: hsl(var(--color-50));
}

.selected-file {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.file-icon {
  font-size: 2rem;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.file-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(var(--color-10));
}

.file-size {
  font-size: 0.75rem;
  color: hsl(var(--color-50));
}

.upload-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.marketplace-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
}

.placeholder-icon {
  font-size: 3rem;
}

.placeholder-text {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.placeholder-title {
  font-size: 1rem;
  font-weight: 600;
  color: hsl(var(--color-20));
}

.placeholder-description {
  font-size: 0.875rem;
  color: hsl(var(--color-50));
  max-width: 320px;
}

.btn-primary,
.btn-secondary {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: opacity 150ms;
}

.btn-primary {
  background-color: hsl(var(--color-primary));
  color: white;
  border: none;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: transparent;
  color: hsl(var(--color-30));
  border: 1px solid hsl(var(--color-70));
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
